import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
	fitnessProfileFormErrorClassName,
	fitnessProfileModalBackdropClassName,
	fitnessProfileModalPanelClassName,
	readingActionButtonClassName
} from '../../FitnessProfile/shared/fitnessProfileReadingStyles';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchUserFitnessProfiles } from '../../../utils/fitnessProfilesApi';
import { sortFitnessProfilesForDisplay } from '../../../utils/sortFitnessProfilesForDisplay';
import { replaceFitnessProfileEndpoint } from '../../../utils/fitnessProfileBloodPressure';
import FitnessProfilePrimaryBadge from '../../FitnessProfile/shared/FitnessProfilePrimaryBadge';
import useSubscriptionGate from '../../../hooks/useSubscriptionGate';

function possessiveProfileName(name) {
	const trimmed = String(name || '').trim();
	if (!trimmed) return 'this';
	return trimmed.endsWith('s') ? `${trimmed}'` : `${trimmed}'s`;
}

function DeleteFitnessProfileModal({ profile, isOpen, onClose, onConfirm, error, deleting }) {
	if (!isOpen || !profile) return null;

	const possessive = possessiveProfileName(profile.name);

	return (
		<div
			className={`${fitnessProfileModalBackdropClassName} max-w-none`}
			onClick={onClose}
			role="presentation"
		>
			<div
				className={`${fitnessProfileModalPanelClassName} max-w-md`}
				onClick={(event) => event.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-labelledby="delete-fitness-profile-modal-title"
			>
				<h3
					id="delete-fitness-profile-modal-title"
					className="text-lg font-semibold text-gray-900 dark:text-dark-text"
				>
					Delete fitness profile?
				</h3>
				<p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
					Are you sure you want to delete{' '}
					<span className="font-medium text-gray-900 dark:text-dark-text">{possessive}</span> fitness
					profile? All profile data will be permanently deleted.
				</p>

				{error && (
					<p className={`mt-3 ${fitnessProfileFormErrorClassName}`} role="alert">
						{error}
					</p>
				)}

				<div className="mt-5 flex flex-wrap justify-end gap-3 border-t border-gray-200 dark:border-dark-border pt-4">
					<button
						type="button"
						onClick={onClose}
						disabled={deleting}
						className="inline-flex min-w-24 items-center justify-center rounded-xl border border-gray-300 dark:border-dark-border px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-bg disabled:opacity-60 transition"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={onConfirm}
						disabled={deleting}
						className="inline-flex min-w-24 items-center justify-center rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60 dark:bg-red-600 dark:hover:bg-red-500 transition"
					>
						{deleting ? 'Deleting…' : 'Yes, delete'}
					</button>
				</div>
			</div>
		</div>
	);
}

export default function FitnessProfilesIndexPage() {
	const { user, csrfToken, endpoints } = useAuth();
	const { guardSubscription } = useSubscriptionGate();
	const [searchParams, setSearchParams] = useSearchParams();
	const invitedEmail = searchParams.get('invite_sent');
	const [profiles, setProfiles] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState('');
	const [profileToDelete, setProfileToDelete] = useState(null);
	const [deleteError, setDeleteError] = useState('');
	const [deleting, setDeleting] = useState(false);

	useEffect(() => {
		async function loadProfiles() {
			if (!user?.id || !endpoints?.get_user_fitness_profiles_endpoint) {
				setProfiles([]);
				setLoadError('');
				setLoading(false);
				return;
			}

			setLoading(true);
			setLoadError('');

			try {
				const data = await fetchUserFitnessProfiles({
					userId: user.id,
					endpoint: endpoints.get_user_fitness_profiles_endpoint,
					csrfToken
				});
				setProfiles(sortFitnessProfilesForDisplay(data));
			} catch (error) {
				setProfiles([]);
				setLoadError(error.message || 'Could not load fitness profiles.');
			} finally {
				setLoading(false);
			}
		}

		loadProfiles();
	}, [endpoints, user?.id]);

	function canDeleteProfile(profile) {
		if (!profile.is_primary) return true;
		return profiles.length === 1;
	}

	function openDeleteModal(profile, event) {
		event.preventDefault();
		event.stopPropagation();
		if (!canDeleteProfile(profile)) return;
		setDeleteError('');
		setProfileToDelete(profile);
	}

	function closeDeleteModal() {
		if (deleting) return;
		setProfileToDelete(null);
		setDeleteError('');
	}

	async function handleConfirmDelete() {
		if (!profileToDelete || !user?.id || !endpoints?.delete_user_fitness_profile_endpoint) {
			setDeleteError('Sign in to delete fitness profiles.');
			return;
		}

		setDeleting(true);
		setDeleteError('');

		try {
			const url = replaceFitnessProfileEndpoint(endpoints.delete_user_fitness_profile_endpoint, {
				userId: user.id,
				fitnessProfileId: profileToDelete.id
			});
			const response = await fetch(url, {
				method: 'DELETE',
				headers: { 'X-CSRF-Token': csrfToken }
			});
			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error((data.errors || []).join(' ') || data.error || 'Could not delete profile.');
			}

			setProfiles((previous) => previous.filter((profile) => profile.id !== profileToDelete.id));
			setProfileToDelete(null);
		} catch (error) {
			setDeleteError(error.message || 'Could not delete profile.');
		} finally {
			setDeleting(false);
		}
	}

	useEffect(() => {
		if (!invitedEmail) return;

		const timeoutId = window.setTimeout(() => {
			setSearchParams((previous) => {
				const next = new URLSearchParams(previous);
				next.delete('invite_sent');
				return next;
			});
		}, 8000);

		return () => window.clearTimeout(timeoutId);
	}, [invitedEmail, setSearchParams]);

	return (
		<div className="container p-4 pt-4 lg:py-14 md:px-20">
			{invitedEmail && (
				<div className="mt-4 rounded-xl border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900 dark:bg-opacity-25 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-200">
					Invitation sent to {invitedEmail}. They can set up their login and complete their profile after
					accepting.
				</div>
			)}
			<div className="mt-4 mb-3 lg:mb-6 flex items-center justify-between gap-4">
				<div className="flex min-w-0 items-center gap-2.5">
					<h1 className="min-w-0 text-xl sm:text-2xl md:text-4xl font-semibold leading-none text-gray-900 dark:text-dark-text">
						Fitness Profiles
					</h1>
					<span className="inline-flex shrink-0 -translate-y-0.5 md:-translate-y-1">
						<Link
							to="/fitness-profiles/new"
							title="Create profile"
							aria-label="Create profile"
							className={readingActionButtonClassName}
							onClick={(event) => {
								if (!guardSubscription()) {
									event.preventDefault();
								}
							}}
						>
							<i className="fa-solid fa-plus text-sm" aria-hidden="true" />
						</Link>
					</span>
				</div>
				<Link
					to="/fitness-hub"
					className="shrink-0 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
				>
					← Fitness Hub
				</Link>
			</div>

			{loading ? (
				<p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading profiles…</p>
			) : !user?.id ? (
				<div className="mt-4 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card p-6">
					<p className="text-sm text-gray-600 dark:text-dark-text-secondary">
						Sign in to view and manage your fitness profiles.
					</p>
				</div>
			) : loadError ? (
				<div className={`mt-4 ${fitnessProfileFormErrorClassName} p-6`}>
					<p className="text-sm text-red-700 dark:text-red-300">{loadError}</p>
				</div>
			) : profiles.length === 0 ? (
				<div className="mt-4 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card p-6">
					<p className="text-sm text-gray-600 dark:text-dark-text-secondary">
						No fitness profiles yet. Use the + button above to create one and start tracking blood
						pressure.
					</p>
				</div>
			) : (
				<div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
					{profiles.map((profile) => (
						<div
							key={profile.id}
							className="flex items-center gap-2 bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200"
						>
							<Link
								to={`/fitness-profiles/${profile.id}`}
								className="flex-1 min-w-0 hover:bg-gray-50 dark:hover:bg-dark-bg -m-2 p-2 rounded-lg transition"
							>
								<div className="flex min-w-0 flex-wrap items-center gap-2">
									<div className="text-lg font-semibold text-gray-900 dark:text-dark-text truncate">
										{profile.name}
									</div>
									{profile.is_primary && <FitnessProfilePrimaryBadge />}
								</div>
							</Link>
							{canDeleteProfile(profile) ? (
								<button
									type="button"
									onClick={(event) => openDeleteModal(profile, event)}
									className="shrink-0 flex h-9 w-9 items-center justify-center rounded-full text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-25 transition"
									aria-label={`Delete ${profile.name} fitness profile`}
									title="Delete fitness profile"
								>
									<i className="fa-solid fa-trash-can text-sm" aria-hidden="true" />
								</button>
							) : (
								<span
									className="shrink-0 flex h-9 w-9 items-center justify-center rounded-full text-gray-300 dark:text-gray-600"
									title="Remove household profiles before deleting your primary profile."
									aria-label="Primary profile cannot be deleted while household profiles exist"
								>
									<i className="fa-solid fa-trash-can text-sm" aria-hidden="true" />
								</span>
							)}
						</div>
					))}
				</div>
			)}

			<DeleteFitnessProfileModal
				profile={profileToDelete}
				isOpen={Boolean(profileToDelete)}
				onClose={closeDeleteModal}
				onConfirm={handleConfirmDelete}
				error={deleteError}
				deleting={deleting}
			/>
		</div>
	);
}
